import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { REPORT_FIELDS, REPORT_OPERATORS, type ReportField, type ReportInput } from '@gwc/shared';
import { api } from '../lib/api';

export function Reports() {
  const [fields, setFields] = useState<ReportField[]>([
    'tasting',
    'date',
    'wine',
    'score',
    'average',
    'rank',
  ]);
  const [filters, setFilters] = useState<ReportInput['filters']>([]);
  const [match, setMatch] = useState<'all' | 'any'>('all');
  const [sort, setSort] = useState<ReportField>('date');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [preview, setPreview] = useState<{ labels: string[]; rows: unknown[][]; total: number }>();
  const run = useMutation({
    mutationFn: async (format: 'json' | 'csv' | 'pdf') => {
      const body = { fields, filters, match, sort, direction, format };
      if (format === 'json') {
        setPreview(await api.post('/reports', body));
        return;
      }
      const file = await api.post<{ base64: string; contentType: string; filename: string }>(
        '/reports',
        body,
      );
      const bytes = Uint8Array.from(atob(file.base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: file.contentType }));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  });
  const fieldOptions = Object.entries(REPORT_FIELDS).map(([key, label]) => (
    <option key={key} value={key}>
      {label}
    </option>
  ));
  return (
    <div className="space-y-5">
      <h1 className="font-display text-3xl">Report builder</h1>
      <p>
        Choose fields and filters for finalized tastings. Rank is calculated across each complete
        tasting before filtering.
      </p>
      <fieldset className="card">
        <legend>Fields (selection order sets column order)</legend>
        <div className="flex flex-wrap gap-4">
          {Object.entries(REPORT_FIELDS).map(([key, label]) => (
            <label key={key}>
              <input
                type="checkbox"
                checked={fields.includes(key as ReportField)}
                onChange={(e) =>
                  setFields((v) =>
                    e.target.checked ? [...v, key as ReportField] : v.filter((f) => f !== key),
                  )
                }
              />{' '}
              {label}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="card space-y-3">
        <label>
          Match{' '}
          <select
            className="field"
            value={match}
            onChange={(e) => setMatch(e.target.value as 'all' | 'any')}
          >
            <option value="all">All filters</option>
            <option value="any">Any filter</option>
          </select>
        </label>
        {filters.map((f, i) => (
          <div key={i} className="flex flex-wrap gap-2">
            <select
              aria-label="Filter field"
              className="field w-auto"
              value={f.field}
              onChange={(e) =>
                setFilters((v) =>
                  v.map((x, j) => (j === i ? { ...x, field: e.target.value as ReportField } : x)),
                )
              }
            >
              {fieldOptions}
            </select>
            <select
              aria-label="Filter operator"
              className="field w-auto"
              value={f.operator}
              onChange={(e) =>
                setFilters((v) =>
                  v.map((x, j) =>
                    j === i ? { ...x, operator: e.target.value as typeof f.operator } : x,
                  ),
                )
              }
            >
              {REPORT_OPERATORS.map((o) => (
                <option key={o} value={o}>
                  {o.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            <input
              aria-label="Filter value"
              className="field w-auto"
              value={f.value}
              maxLength={200}
              onChange={(e) =>
                setFilters((v) => v.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))
              }
            />
            <button
              className="btn-ghost"
              onClick={() => setFilters((v) => v.filter((_, j) => i !== j))}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          className="btn-ghost"
          disabled={filters.length >= 20}
          onClick={() =>
            setFilters((v) => [...v, { field: 'producer', operator: 'includes', value: '' }])
          }
        >
          Add filter
        </button>
        <label className="block">
          Sort by{' '}
          <select
            className="field"
            value={sort}
            onChange={(e) => setSort(e.target.value as ReportField)}
          >
            {fieldOptions}
          </select>
        </label>
        <select
          aria-label="Sort direction"
          className="field"
          value={direction}
          onChange={(e) => setDirection(e.target.value as 'asc' | 'desc')}
        >
          <option value="desc">Descending</option>
          <option value="asc">Ascending</option>
        </select>
      </div>
      <div className="flex gap-3">
        {(['json', 'csv', 'pdf'] as const).map((format) => (
          <button
            className="btn-primary"
            key={format}
            disabled={!fields.length || run.isPending}
            onClick={() => run.mutate(format)}
          >
            {format === 'json' ? 'Preview' : `Export ${format.toUpperCase()}`}
          </button>
        ))}
      </div>
      {run.error && <p role="alert">{run.error.message}</p>}
      {preview && (
        <div className="overflow-x-auto">
          <p>{preview.total} records</p>
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {preview.labels.map((l) => (
                  <th className="p-2" key={l}>
                    {l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.rows.slice(0, 100).map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td className="border-t border-ink-800 p-2" key={j}>
                      {String(cell ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {preview.total > 100 && (
            <p>Showing the first 100 records. Exports include all results.</p>
          )}
        </div>
      )}
    </div>
  );
}
